import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const SPECIALIZATIONS = [
  { value: 'general', label: 'General Mechanic' },
  { value: 'electrical', label: 'Electrical Systems' },
  { value: 'engine', label: 'Engine Specialist' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'brakes', label: 'Brake Systems' },
  { value: 'ac', label: 'Air Conditioning' },
  { value: 'bodywork', label: 'Body Work' },
  { value: 'diagnostic', label: 'Diagnostic Specialist' },
  { value: 'mot', label: 'MOT Testing' },
  { value: 'hybrid', label: 'Hybrid/Electric Vehicles' },
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'busy', label: 'Busy' },
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'break', label: 'On Break' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'vacation', label: 'On Vacation' },
];

const Technicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [technicianToDelete, setTechnicianToDelete] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    employee_id: '',
    specializations: [],
    certification_level: '',
    years_of_experience: 0,
    status: 'available',
    current_location: '',
    max_concurrent_jobs: 3,
    hourly_rate: '',
    shift_start: '',
    shift_end: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    hired_date: '',
  });

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/v1/technicians/');
      setTechnicians(response.data);
    } catch (err) {
      setError('Failed to load technicians: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpecializationChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      specializations: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      let response;
      if (editingTechnician) {
        response = await api.put(`/v1/technicians/${editingTechnician.id}/`, formData);
      } else {
        response = await api.post('/v1/technicians/', formData);
      }

      setSuccess(editingTechnician ? 'Technician updated successfully!' : 'Technician created successfully!');
      setOpenDialog(false);
      resetForm();
      fetchTechnicians();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setFormData({
      first_name: technician.full_name.split(' ')[0] || '',
      last_name: technician.full_name.split(' ').slice(1).join(' ') || '',
      email: technician.email,
      phone: technician.phone || '',
      employee_id: technician.employee_id,
      specializations: technician.specializations || [],
      certification_level: technician.certification_level || '',
      years_of_experience: technician.years_of_experience || 0,
      status: technician.status,
      current_location: technician.current_location || '',
      max_concurrent_jobs: technician.max_concurrent_jobs || 3,
      hourly_rate: technician.hourly_rate || '',
      shift_start: technician.shift_start || '',
      shift_end: technician.shift_end || '',
      emergency_contact: technician.emergency_contact || '',
      emergency_phone: technician.emergency_phone || '',
      notes: technician.notes || '',
      hired_date: technician.hired_date || '',
    });
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!technicianToDelete) return;

    try {
      await api.delete(`/v1/technicians/${technicianToDelete.id}/`);
      setSuccess('Technician deleted successfully!');
      setDeleteConfirmOpen(false);
      setTechnicianToDelete(null);
      fetchTechnicians();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setDeleteConfirmOpen(false);
      setTechnicianToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      employee_id: '',
      specializations: [],
      certification_level: '',
      years_of_experience: 0,
      status: 'available',
      current_location: '',
      max_concurrent_jobs: 3,
      hourly_rate: '',
      shift_start: '',
      shift_end: '',
      emergency_contact: '',
      emergency_phone: '',
      notes: '',
      hired_date: '',
    });
    setEditingTechnician(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'success',
      busy: 'warning',
      unavailable: 'error',
      break: 'info',
      sick: 'error',
      vacation: 'secondary'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <EngineeringIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Technicians
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpenDialog(true);
          }}
        >
          Add Technician
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Specializations</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {technicians.map((technician) => (
              <TableRow key={technician.id} hover>
                <TableCell>{technician.full_name}</TableCell>
                <TableCell>{technician.employee_id}</TableCell>
                <TableCell>{technician.email}</TableCell>
                <TableCell>{technician.phone}</TableCell>
                <TableCell>
                  <Chip
                    label={technician.status}
                    color={getStatusColor(technician.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {technician.specialization_display?.slice(0, 2).map((spec, index) => (
                      <Chip key={index} label={spec} size="small" variant="outlined" />
                    ))}
                    {technician.specialization_display?.length > 2 && (
                      <Chip label={`+${technician.specialization_display.length - 2}`} size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{technician.current_location}</TableCell>
                <TableCell>{technician.years_of_experience} years</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(technician)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTechnicianToDelete(technician);
                      setDeleteConfirmOpen(true);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {technicians.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No technicians found. Add your first technician to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTechnician ? 'Edit Technician' : 'Add New Technician'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </Grid>
              {!editingTechnician && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    helperText="Leave blank to use default password"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employee ID"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Current Location"
                  name="current_location"
                  value={formData.current_location}
                  onChange={handleInputChange}
                  placeholder="e.g., Bay 1, Workshop A"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Specializations</InputLabel>
                  <Select
                    multiple
                    value={formData.specializations}
                    onChange={handleSpecializationChange}
                    input={<OutlinedInput label="Specializations" />}
                    renderValue={(selected) =>
                      selected.map(s => SPECIALIZATIONS.find(spec => spec.value === s)?.label || s).join(', ')
                    }
                  >
                    {SPECIALIZATIONS.map((spec) => (
                      <MenuItem key={spec.value} value={spec.value}>
                        <Checkbox checked={formData.specializations.indexOf(spec.value) > -1} />
                        <ListItemText primary={spec.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Certification Level"
                  name="certification_level"
                  value={formData.certification_level}
                  onChange={handleInputChange}
                  placeholder="e.g., Level 3 NVQ"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Years of Experience"
                  name="years_of_experience"
                  type="number"
                  value={formData.years_of_experience}
                  onChange={handleInputChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Max Concurrent Jobs"
                  name="max_concurrent_jobs"
                  type="number"
                  value={formData.max_concurrent_jobs}
                  onChange={handleInputChange}
                  inputProps={{ min: 1, max: 500 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Hourly Rate"
                  name="hourly_rate"
                  type="number"
                  value={formData.hourly_rate}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Shift Start"
                  name="shift_start"
                  type="time"
                  value={formData.shift_start}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Shift End"
                  name="shift_end"
                  type="time"
                  value={formData.shift_end}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Phone"
                  name="emergency_phone"
                  value={formData.emergency_phone}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Hired Date"
                  name="hired_date"
                  type="date"
                  value={formData.hired_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTechnician ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete technician "{technicianToDelete?.full_name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Technicians;